import type React from 'react'
import { ProjList, type ProjListItem } from '../../components/ProjList/ProjList'
import { useGhostProjects } from '../../hooks/useGhostProjects'

const ROW_BASE =
	'grid grid-cols-[auto_1fr] md:grid-cols-[80px_1fr_240px_64px] gap-4 md:gap-8 px-7 py-8 border-b-2 border-ink last:border-b-0 items-center ' +
	'hover:bg-hazard hover:pl-10 transition-[padding,background] duration-150 focus-ring'

const fallback = (
	<a
		href="https://blog.luizcavalieri.dev?tag=projects"
		target="_blank"
		rel="noopener noreferrer"
		className={ROW_BASE}
	>
		<span className="font-display-cond text-4xl font-black leading-none tracking-[-0.02em] text-ink">→</span>
		<span className="font-display font-extrabold uppercase text-[28px] leading-[1.1] text-ink">
			View all projects
			<span className="block font-serif font-medium normal-case italic text-[15px] text-ink-dim mt-1.5 leading-[1.45]">
				Browse the full project archive on the blog.
			</span>
		</span>
		<span className="font-mono text-[11px] uppercase text-ink-2 font-medium">External ↗</span>
		<span className="font-display-cond text-[32px] font-black text-right text-ink">→</span>
	</a>
)

export default function ProjectsPage(): React.ReactElement {
	const { projects, loading, error } = useGhostProjects()

	const items: ProjListItem[] = !loading && !error
		? projects.map((p) => ({
				id: p.id,
				title: p.title,
				excerpt: p.custom_excerpt ?? p.excerpt,
				meta: new Date(p.published_at).getFullYear().toString(),
				href: p.url || `${import.meta.env.VITE_GHOST_URL}/${p.slug}/`,
				external: true,
			}))
		: []

	return (
		<div className="px-6 md:px-12 py-16 md:py-24 max-w-[1600px] mx-auto">
			<p className="font-mono uppercase text-[11px] tracking-widest text-ink-2 mb-4">
				Selected Work · On Record
			</p>
			<h1 className="font-display font-extrabold text-[clamp(40px,5vw,84px)] leading-none text-ink mb-4">
				A short list, kept{' '}
				<span className="font-serif italic font-normal">short</span>
				{' '}on purpose.
			</h1>
			<p className="font-serif italic text-[18px] text-ink-dim mb-12">
				Most of what I do isn't visible. These are the few that are.
			</p>

			{loading ? (
				<div className="max-w-[1600px] bg-paper border-2 border-ink">
					<div className="px-7 py-8 font-mono text-[11px] text-ink-2">Loading…</div>
				</div>
			) : (
				<ProjList items={items} fallback={fallback} />
			)}
		</div>
	)
}
